import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n";
import LanguagePicker from "@/components/LanguagePicker";
import { fetchJobs } from "@/services/preparation";
import PreparationPage from "@/pages/PreparationPage";
import ProductionPage from "@/pages/ProductionPage";
import ReceivingPage from "@/pages/ReceivingPage";
import SupervisorPage from "@/pages/SupervisorPage";
import StockPage from "@/pages/StockPage";
import CartsPage, { cartNumber } from "@/pages/CartsPage";
import CommercialPage from "@/pages/CommercialPage";
import AccessPage from "@/pages/AccessPage";
import HomePage from "@/pages/HomePage";
import PhotoStation from "@/components/PhotoStation";
import { isDesk } from "@/lib/device";
import { useWakeLock } from "@/lib/wakeLock";

// The two tools Ludovic built, under one roof: Z Furniture (deliveries) and Z Production Rate and
// Inventory (making kits to stock). They share the warehouse filter because a fitter works in one
// warehouse all day and should not pick it twice.
const TABS = ["preparation", "supervisor", "stock", "carts", "commercial", "production", "receiving"] as const;
const WAREHOUSE_KEY = "myotaku-warehouse:warehouse";
const STATION_KEY = "myotaku-warehouse:photo-station";
// His `WHS`: the warehouses in his order; any other the planning names comes after.
const WH_ORDER = [1, 67, 4, 34];
type Tab = (typeof TABS)[number];

export default function Shell() {
  const { t } = useT();
  const { staff, signOut } = useAuth();
  // A cart's QR label opens the app on that cart: …/?cart=42, CH-001 to CH-999.
  const scannedCart = useMemo(() => cartNumber(new URLSearchParams(window.location.search).get("cart") ?? ""), []);
  // A scan opens its cart once: the address forgets ?cart= and coming back to Carts later opens nothing.
  const cartOnce = useRef(scannedCart);
  useEffect(() => {
    if (!scannedCart) return;
    const u = new URL(window.location.href);
    u.searchParams.delete("cart");
    window.history.replaceState(window.history.state, "", u.pathname + u.search + u.hash);
  }, [scannedCart]);
  // A Slack alert's "Ouvrir" link opens its tab: …/?tab=supervisor, …/?tab=production.
  const linkedTab = useMemo(() => {
    const v = new URLSearchParams(window.location.search).get("tab");
    return (TABS as readonly string[]).includes(v ?? "") ? (v as Tab) : null;
  }, []);
  // Only the modules this person uses (Charles #7 / #12); an admin also gets "Accès". The app opens on
  // his home module unless a link or a scanned cart asks for one he uses.
  const tabs = useMemo(() => {
    const mine = TABS.filter((k) => staff?.modules?.includes(k) ?? true);
    return staff?.is_admin ? [...mine, "access" as const] : mine;
  }, [staff]);
  const opening = (scannedCart ? "carts" : linkedTab) as Tab | "access" | null;
  const [tab, setTab] = useState<Tab | "access" | "home">(
    opening && tabs.includes(opening) ? opening : ((staff?.home_module as Tab | undefined) ?? tabs[0] ?? "preparation"),
  );
  const [stockReturn, setStockReturn] = useState<number | null>(null);
  // "Ouvrir la fiche" on a read supplier document opens that delivery's job.
  const [openJob, setOpenJob] = useState<number | null>(null);
  // His warehouse choice is remembered on this device.
  const [warehouse, setWarehouseState] = useState<number | null>(() => {
    try { return Number(localStorage.getItem(WAREHOUSE_KEY)) || null; } catch { return null; }
  });
  const setWarehouse = (id: number | null) => {
    setWarehouseState(id);
    try { localStorage.setItem(WAREHOUSE_KEY, id ? String(id) : ""); } catch { /* this tab only */ }
  };

  // His Montage chips count the OPEN jobs: running, paused, or not started with the assembly day
  // today or past — his Today tab without the jobs finished today. Upcoming, done and archived are not.
  // His live list: every device sees another's start, pause, finish and archive within half a minute.
  const jobs = useQuery({ queryKey: ["prep-jobs", true], queryFn: () => fetchJobs(true), refetchInterval: 30_000 });
  const open = useMemo(() => (jobs.data?.jobs ?? []).filter((j) => j.tab === "today"), [jobs.data]);
  const counts = useMemo(() => {
    const m = new Map<number, number>();
    open.forEach((j) => { if (j.warehouse) m.set(j.warehouse.id, (m.get(j.warehouse.id) || 0) + 1); });
    return m;
  }, [open]);
  const total = open.length;
  // His `wake` at the level of the whole app: the screen stays on from the moment a job runs, whichever
  // screen is shown, and is taken again when the page comes back while any job still runs.
  const anyRunning = (jobs.data?.jobs ?? []).some((j) => j.run?.status === "running");
  useWakeLock(anyRunning, anyRunning);
  const warehouses = useMemo(() => [...(jobs.data?.warehouses ?? [])].sort((a, b) =>
    (WH_ORDER.indexOf(a.id) + 1 || 99) - (WH_ORDER.indexOf(b.id) + 1 || 99) || a.name.localeCompare(b.name)), [jobs.data]);
  // A page opens only inside this person's modules, whatever asked for it (a link, a scanned cart, a
  // button on another page): the server refuses the rest anyway, and the header has no button for it.
  const shows = (k: Tab | "access") => tab === k && (tabs as readonly string[]).includes(k);
  // "Open the job" only for people who can see the Montage.
  const openJobFor = (id: number) => { setOpenJob(id); setTab("preparation"); };
  const canOpenJob = tabs.includes("preparation");
  // A cart's "Stock intake for this return" only for people who write Stock: the intake is Stock's to
  // write, and his button would also log a "Stock intake" line no one can then make (the server agrees).
  const canTakeIntoStock = tabs.includes("stock") && !staff?.read_only_modules?.includes("stock");
  // His "📱 Poste photo": a tablet that was the PC's camera is one again after a reload. Never on a PC,
  // and only for someone who can answer a request: the Montage or Receiving, not read-only.
  const canStation = !isDesk() && (["preparation", "receiving"] as const)
    .some((k) => tabs.includes(k) && !staff?.read_only_modules?.includes(k));
  const [station, setStationState] = useState<boolean>(() => {
    try { return !isDesk() && localStorage.getItem(STATION_KEY) === "1"; } catch { return false; }
  });
  const setStation = (on: boolean) => {
    setStationState(on);
    try { localStorage.setItem(STATION_KEY, on ? "1" : ""); } catch { /* this tab only */ }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="flex flex-wrap items-baseline gap-x-4 gap-y-2 border-b border-slate-200 bg-white px-5 py-3">
        <h1 className="text-lg font-semibold tracking-tight text-slate-900">{t("app.title")}</h1>
        {/* Wraps on a phone: the page never scrolls sideways, whatever the number of modules. */}
        <nav className="flex min-w-0 flex-wrap gap-1">
          {/* His "⌂ Accueil", always there: the home screen with a tile per section. */}
          <button onClick={() => setTab("home")} title={t("nav.home")}
            className={`rounded-lg px-2.5 py-1 text-sm transition ${tab === "home" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>⌂ {t("nav.home")}</button>
          {tabs.map((k) => (
            <button key={k} onClick={() => setTab(k)}
              className={`rounded-lg px-3 py-1 text-sm transition ${
                tab === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {t(`nav.${k}`)}
            </button>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-3 text-sm text-slate-500">
          {canStation && (
            <button onClick={() => setStation(true)} className="rounded-lg px-2.5 py-1 text-sm ring-1 ring-slate-300 hover:bg-slate-100">{t("st.btn")}</button>
          )}
          <LanguagePicker />
          <span className="hidden sm:inline">{staff?.name || staff?.email}</span>
          <button onClick={() => void signOut()} className="underline underline-offset-2 hover:text-slate-900">
            {t("nav.signOut")}
          </button>
        </div>
      </header>

      {/* One warehouse choice for every section (a fitter works in one warehouse all day); his open-job
          counts only where they mean something, on the Montage. */}
      {tab !== "home" && tab !== "access" && (
        <div className="flex flex-wrap gap-2 border-b border-slate-200 bg-white px-5 py-2.5">
          <Chip label={t("wh.all")} count={tab === "preparation" ? total : null} active={warehouse === null} onClick={() => setWarehouse(null)} />
          {warehouses.map((w) => (
            <Chip key={w.id} label={w.name} count={tab === "preparation" ? counts.get(w.id) || 0 : null}
              active={warehouse === w.id} onClick={() => setWarehouse(w.id)} />
          ))}
        </div>
      )}

      {station && canStation && (
        <PhotoStation onLeave={() => setStation(false)}
          onReceiving={() => { setStation(false); if (tabs.includes("receiving")) setTab("receiving"); }} />
      )}

      <div className="px-5 py-6">
        {tabs.length === 0 && <p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200">{t("access.noModule")}</p>}
        {shows("preparation") && <PreparationPage key={openJob ?? 0} warehouseId={warehouse} jobs={jobs} initialEvent={openJob} />}
        {shows("supervisor") && <SupervisorPage warehouseId={warehouse} onOpenJob={canOpenJob ? openJobFor : undefined} />}
        {shows("stock") && <StockPage warehouseId={warehouse} initialReturnId={stockReturn} />}
        {shows("carts") && (
          <CartsPage warehouseId={warehouse} initialCart={cartOnce.current} onCartOpened={() => { cartOnce.current = null; }}
            // His "Opened from Carts": Intake with the cart's return chosen, in that return's warehouse.
            onTakeIntoStock={canTakeIntoStock
              ? (id, wh) => { if (wh && wh !== warehouse) setWarehouse(wh); setStockReturn(id); setTab("stock"); } : undefined} />
        )}
        {shows("commercial") && <CommercialPage warehouseId={warehouse} />}
        {shows("production") && <ProductionPage warehouseId={warehouse} />}
        {shows("access") && <AccessPage />}
        {tab === "home" && <HomePage tabs={tabs} onOpen={(k) => setTab(k as Tab | "access")} />}
        {shows("receiving") && (
          <ReceivingPage warehouseId={warehouse} onOpenJob={canOpenJob ? openJobFor : undefined} />
        )}
      </div>
    </div>
  );
}

function Chip({ label, count, active, onClick }: { label: string; count: number | null; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} aria-pressed={active}
      className={`rounded-full px-3 py-1 text-sm transition ${
        active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
      {label}{count != null && <span className={active ? "text-slate-300" : "text-slate-400"}> {count}</span>}
    </button>
  );
}
